import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import apiClient from '../../api/client'; // Import apiClient
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PageTransitionWrapper from '../../components/layout/PageTransitionWrapper';
import { FaChild, FaRunning, FaGraduationCap, FaFemale, FaMale, FaBlind } from 'react-icons/fa';

// Definisi konfigurasi Tab Pelkat agar sesuai dengan struktur identitas GPIB & API
const PELKAT_TABS = [
    { id: 'pa', name: 'PA', slug: 'pa', fullName: 'Pelayanan Anak', color: 'text-pink-500', bgColor: 'bg-pink-50', border: 'border-pink-500', icon: <FaChild /> },
    { id: 'pt', name: 'PT', slug: 'pt', fullName: 'Pelayanan Taruna', color: 'text-teal-500', bgColor: 'bg-teal-50', border: 'border-teal-500', icon: <FaRunning /> },
    { id: 'gp', name: 'GP', slug: 'gp', fullName: 'Gerakan Pemuda', color: 'text-blue-500', bgColor: 'bg-blue-50', border: 'border-blue-500', icon: <FaGraduationCap /> },
    { id: 'pkp', name: 'PKP', slug: 'pkp', fullName: 'Pelayanan Kategorial Perempuan', color: 'text-purple-500', bgColor: 'bg-purple-50', border: 'border-purple-500', icon: <FaFemale /> },
    { id: 'pkb', name: 'PKB', slug: 'pkb', fullName: 'Pelayanan Kategorial Pria', color: 'text-indigo-500', bgColor: 'bg-indigo-50', border: 'border-indigo-500', icon: <FaMale /> },
    { id: 'pklu', name: 'PKLU', slug: 'pklu', fullName: 'Pelayanan Lansia Usia Lanjut', color: 'text-amber-600', bgColor: 'bg-amber-50', border: 'border-amber-600', icon: <FaBlind /> },
];

const Pelkat = () => {
    const [activeTab, setActiveTab] = useState(PELKAT_TABS[0]); // Default ke PA
    const [pelkatData, setPelkatData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const getCleanBodyHtml = (rawHtml) => {
        if (!rawHtml) return '';
        return DOMPurify.sanitize(rawHtml, {
            FORBID_ATTR: ['style', 'class'],
            FORBID_TAGS: ['script'],
        });
    };
    // Fetch data dari API setiap kali activeTab berubah
    useEffect(() => {
        const fetchPelkatData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await apiClient.getPelkat(activeTab.slug);
                setPelkatData(response);
            } catch (err) {
                console.error('Error fetching pelkat data:', err);
                setError('Gagal memuat data pengurus Pelkat. Silakan coba lagi nanti.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchPelkatData();
    }, [activeTab]);

    return (
        <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">

                {/* 🌟 Header Halaman */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-10 relative overflow-hidden border-b-8 border-green-600">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-50/40 to-white transform skew-x-3 -translate-x-1/2 opacity-70"></div>
                    <div className="relative z-10 text-center">
                        <h1 className="text-4xl sm:text-5xl font-black text-gray-900 mb-2">
                            PENGURUS PELKAT
                        </h1>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto font-medium">
                            Pelayanan Kategorial Jemaat GPIB Kasih Karunia Badung Bali.
                        </p>
                    </div>
                </div>

                {/* 🗂️ Navigasi Tab (6 Pelkat) */}
                <div className="mt-8 bg-white rounded-2xl shadow-xl p-4 sm:p-6 relative">
                    {/* <div className="bg-white rounded-xl shadow-lg p-6 mb-8"> */}
                    <h2 className="text-center font-bold text-gray-700 mb-4 border-b pb-2">Pilih Kategori</h2>

                    <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                        {PELKAT_TABS.map((tab) => {
                            const isActive = activeTab.id === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab)}
                                    className={`flex items-center space-x-2 px-4 py-3 rounded-xl font-extrabold text-sm sm:text-base transition-all duration-200 shadow-sm
                                    ${isActive
                                            ? `${tab.bgColor} ${tab.color} border-2 ${tab.border} scale-105 shadow-md`
                                            : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-800'
                                        }`}
                                >
                                    <span className="text-lg">{tab.icon}</span>
                                    <span>{tab.name}</span>
                                </button>
                            );
                        })}
                    </div>
                    {/* </div> */}
                </div>

                {/* 📋 Area Konten Utama */}
                <div className="mt-8 bg-white rounded-2xl shadow-xl p-6 sm:p-10 min-h-[400px] relative">
                    <div className="mb-6 border-b pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div>
                            <h2 className={`text-2xl font-black ${activeTab.color}`}>
                                {activeTab.fullName} ({activeTab.name})
                            </h2>
                            {/* ✅ pakai title dari API kalau ada, sebagai sub-judul yang lebih spesifik */}
                            {pelkatData && pelkatData.title && (
                                <p className="text-sm text-gray-500 mt-1">{pelkatData.title}</p>
                            )}
                        </div>
                        {/* ✅ pakai logo dari API kalau ada, fallback ke icon lama */}
                        {pelkatData && pelkatData.logo ? (
                            <img
                                src={pelkatData.logo}
                                alt={`Logo ${activeTab.fullName}`}
                                className="hidden sm:block w-20 h-20 object-contain"
                            />
                        ) : (
                            <div className={`hidden sm:flex p-3 rounded-full ${activeTab.bgColor} ${activeTab.color} text-2xl`}>
                                {activeTab.icon}
                            </div>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                            <LoadingSpinner />
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <p className="text-red-500 font-semibold">{error}</p>
                        </div>
                    ) : (
                        <PageTransitionWrapper>
                            {/* {komisiData && komisiData.body ? (
                                        <div
                                            className="komisi-body overflow-x-auto"
                                            dangerouslySetInnerHTML={{ __html: getCleanBodyHtml(komisiData.body) }}
                                        />
                                    ) : (
                                        <div className="text-center py-16 text-gray-400">
                                            <p className="text-lg font-medium mb-1">Susunan Komisi Belum Tersedia</p>
                                            <p className="text-sm">Data untuk komisi ini sedang dalam proses pembaharuan.</p>
                                        </div>
                                    )} */}
                            {pelkatData && pelkatData.body ? (
                                <div
                                    className="komisi-body overflow-x-auto"
                                    dangerouslySetInnerHTML={{ __html: getCleanBodyHtml(pelkatData.body) }}
                                />
                            ) : (
                                // Tampilan alternatif jika body kosong di API
                                <div className="text-center py-16 text-gray-400">
                                    <p className="text-lg font-medium mb-1">Susunan Pengurus Belum Tersedia</p>
                                    <p className="text-sm">Data fungsionaris untuk kategorial ini sedang dalam proses pembaharuan.</p>
                                </div>
                            )}
                        </PageTransitionWrapper>
                    )}
                </div>

            </div>
        </div>
    );
};

export default Pelkat;